// PERFORMANCE OPTIMIZATION ADDED - Suspense for lazy loading
import React, { createContext, useContext, Suspense } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import PageTransition from "../components/PageTransition";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import ScrollToTop from "../components/ScrollToTop";
import BackButton from "../components/BackButton";
import Loading from "../components/Loading";
import { AuthProvider, useAuth } from "../context/AuthContext";
import { CartProvider } from "../context/CartContext";
import RequireAuth from "../components/RequireAuth";
import AdminLayoutWrapper from "../components/AdminLayoutWrapper";
import { publicRoutes } from "./publicRoutes";
import { protectedRoutes } from "./protectedRoutes";
// PERFORMANCE OPTIMIZATION ADDED - Keep NotFound as regular import for 404 handling
import NotFound from "../pages/NotFound";
import SEO from "../components/SEO";

interface AppContextType {
  theme: "light" | "dark";
  setTheme: (theme: "light" | "dark") => void;
}

const AppContext = createContext<AppContextType | null>(null);

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useAppContext must be used within AppProvider");
  }
  return context;
};

const AppContent: React.FC = () => {
  const location = useLocation();
  const { role } = useAuth();
  const isAdmin = role === "admin";
  const isAdminRoute = location.pathname.startsWith("/admin");

  // Check if we're on checkout page
  const isCheckoutPage = location.pathname === "/checkout";
  
  // Check if we're on a 404 page (no route matches)
  const is404Page = React.useMemo(() => {
    const pathname = location.pathname;
    
    // Get all route paths
    const allRoutes = [
      ...publicRoutes.map(r => r.path),
      ...protectedRoutes.map(r => r.path),
    ];
    
    // Check if pathname matches any route pattern
    const matchesRoute = allRoutes.some(route => {
      // Convert route pattern to regex (handle dynamic segments)
      let pattern = route
        .replace(/:[^/]+/g, '[^/]+') // Replace :id with [^/]+
        .replace(/\//g, '\\/'); // Escape slashes
      
      // Handle root route specially
      if (route === '/') {
        return pathname === '/';
      }
      
      const regex = new RegExp(`^${pattern}$`);
      return regex.test(pathname);
    });
    
    // 404 if no route matches
    return !matchesRoute;
  }, [location.pathname]);
  
  // Pages that should hide header and footer
  const shouldHideHeaderFooter = isCheckoutPage || is404Page;

  return (
    <>
      <ScrollToTop />
      {/* Admin screens (including /admin-login) never belong in search results */}
      {isAdminRoute && (
        <SEO
          title="Admin | Golden Harvest Mango Farm"
          description="Golden Harvest Mango Farm administration."
          url={location.pathname}
          noindex
          nofollow
        />
      )}
      {!shouldHideHeaderFooter && <Navbar />}
      {(!shouldHideHeaderFooter || isCheckoutPage) && <BackButton />}

      <main className={isAdmin || isAdminRoute || shouldHideHeaderFooter ? "" : "pt-16 min-h-screen"} style={{ overflowX: "hidden", width: "100%", maxWidth: "100%", marginTop: 0 }}>
        {/* PERFORMANCE OPTIMIZATION ADDED - Suspense wrapper for lazy loaded routes with optimized loading fallback */}
        {/* PREMIUM MOTION - Admin pages share one key (no re-animation on data-heavy screens). Route changes fade the old page out, then rise the new one in */}
        <AnimatePresence mode="wait" initial={false}>
        <PageTransition key={isAdminRoute ? "admin" : location.pathname}>
        <Suspense fallback={<Loading />}>
          <Routes location={location}>
            {publicRoutes.map((route) => (
              <Route
                key={route.path}
                path={route.path}
                element={
                  isAdmin ? (
                    <AdminLayoutWrapper>{route.element}</AdminLayoutWrapper>
                  ) : (
                    route.element
                  )
                }
              />
            ))}

            {protectedRoutes.map((route) => {
              const isAdminOnly = route.allowedRoles.length === 1 && route.allowedRoles[0] === "admin";
              const isSharedRoute = route.allowedRoles.includes("admin") && route.allowedRoles.includes("user");
              return (
                <Route
                  key={route.path}
                  path={route.path}
                  element={
                    <RequireAuth allowedRoles={route.allowedRoles}>
                      {isAdmin ? (
                        <AdminLayoutWrapper>{route.element}</AdminLayoutWrapper>
                      ) : isAdminOnly ? (
                        <AdminLayoutWrapper>{route.element}</AdminLayoutWrapper>
                      ) : isSharedRoute ? (
                        <AdminLayoutWrapper>{route.element}</AdminLayoutWrapper>
                      ) : (
                        route.element
                      )}
                    </RequireAuth>
                  }
                />
              );
            })}

            {/* Catch-all route for 404 */}
            <Route
              path="*"
              element={
                isAdmin ? (
                  <AdminLayoutWrapper>
                    <NotFound />
                  </AdminLayoutWrapper>
                ) : (
                  <NotFound />
                )
              }
            />
          </Routes>
        </Suspense>
        </PageTransition>
        </AnimatePresence>
      </main>

      {!(isAdmin || isAdminRoute || shouldHideHeaderFooter) && <Footer />}
    </>
  );
};

export const AppRoutes: React.FC = () => {
  const [theme, setTheme] = React.useState<"light" | "dark">("light");

  return (
    <AppContext value={{ theme, setTheme }}>
      <Router>
        <div className="App">
          <AuthProvider>
            <CartProvider>
              <AppContent />
            </CartProvider>
          </AuthProvider>
        </div>
      </Router>
    </AppContext>
  );
};

export default AppRoutes;


